import { useEffect, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import Lottie from 'lottie-react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';

import Api from '../../services';
import pb from '../../assets/images/pb.svg';
import invalidQrCode from '../../assets/images/invalid-qr.svg';
import confettiAnimation from '../../assets/animations/confetti.json';
import { RootState } from '../../redux/store';
import { locationPath, saveAuthReturnTo } from '../../helpers/auth-return';

const ACTIVE_STATE_SUCCESS = 'success';
const ACTIVE_STATE_CANT_ACTIVATE = 'pb_cannot_be_activated';

interface ItemDetails {
  uuid: string;
  is_activatable: boolean;
  duration: number | null;
}

function SuccessView() {
  const { t } = useTranslation();
  return (
    <div className='flex h-[calc(100vh-4rem)] flex-col items-center justify-center'>
      <h2 className='text-center text-2xl font-bold leading-9 tracking-tight text-gray-900'>{t('activation.success.title')}</h2>
      <p className='text-center mt-3 mb-10 text-base font-medium text-secondary'>{t('activation.success.subtitle')}</p>
      <div className='relative'>
        <Lottie animationData={confettiAnimation} loop className='h-full w-full' />
        <img src={pb} alt='chaizer' className='absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2' />
      </div>
    </div>
  );
}

function ActivationErrorView() {
  const { t } = useTranslation();
  return (
    <div className='flex h-[calc(100vh-4rem)] flex-col items-center justify-center'>
      <h2 className='text-center text-2xl font-bold leading-9 tracking-tight text-gray-900'>{t('activation.cantActivate.title')}</h2>
      <p className='text-center mt-3 mb-10 text-base font-medium text-secondary'>{t('activation.cantActivate.subtitle')}</p>
      <img src={pb} alt='chaizer' />
    </div>
  );
}

function Loader() {
  return (
    <div className='flex h-[calc(100vh-4rem)] items-center justify-center'>
      <svg
        className='animate-spin h-20 w-20 text-primary'
        xmlns='http://www.w3.org/2000/svg'
        fill='none'
        viewBox='0 0 24 24'
      >
        <circle className='opacity-25' cx='12' cy='12' r='10' stroke='currentColor' strokeWidth='4' />
        <path
          className='opacity-75'
          fill='currentColor'
          d='M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z'
        />
      </svg>
    </div>
  );
}

function ActivationView({
  itemDetails, handleActivate, isActivating, error, authenticated,
}: {
  itemDetails: ItemDetails,
  handleActivate: () => void,
  isActivating: boolean,
  error: string | null,
  authenticated: boolean,
}) {
  const { t } = useTranslation();
  return (
    <div className='flex flex-col items-center text-center'>
      <h2 className='text-2xl font-bold leading-9 tracking-tight text-gray-900'>
        {t('activation.itemDetails.title')}
      </h2>

      <img src={pb} alt='chaizer' className='my-4 mx-auto' />

      <p className='text-base font-medium text-secondary'>
        {t('activation.itemDetails.uuid')}
        {' '}
        {itemDetails.uuid}
      </p>

      {itemDetails.is_activatable
        && (
          <p className='mt-1 text-base font-medium text-secondary'>
            {t('activation.itemDetails.grantedDuration', { duration: itemDetails.duration || 0 })}
          </p>
        )}

      <div className='mt-6 flex-col justify-center'>
        <button
          type='button'
          onClick={handleActivate}
          disabled={isActivating || (authenticated && !itemDetails.is_activatable)}
          className='rounded-md bg-primary px-3 py-1.5 text-sm font-semibold leading-6 text-white shadow-sm
                     hover:bg-primary-dark focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2
                     focus-visible:outline-primary-dark disabled:opacity-50 disabled:cursor-not-allowed'
        >
          {isActivating
            ? t('activation.activateButton.activating')
            : t(authenticated ? 'activation.activateButton.activate' : 'activation.activateButton.loginToActivate')}
        </button>

        <div className='flex items-center justify-center mt-5'>
          <a
            href='https://wa.me/966550132719'
            target='_blank'
            rel='noopener noreferrer'
            className='inline-flex items-center gap-2 text-base font-bold text-primary underline'
          >
            <span>{t('shared.contactSupport')}</span>
            <svg className='h-5 w-5' fill='currentColor' viewBox='0 0 24 24' aria-hidden='true'>
              <title>WhatsApp</title>
              <path d='M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04
              1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z'
              />
            </svg>
          </a>
        </div>
      </div>
      {error && (
        <p className='mt-4 text-center text-red-500'>{error}</p>
      )}
    </div>
  );
}

function NotFoundView() {
  const { t } = useTranslation();
  return (
    <div className='flex h-[calc(100vh-4rem)] flex-col items-center justify-center'>
      <h2 className='text-center text-2xl font-bold leading-9 tracking-tight text-gray-900'>{t('activation.notFound.title')}</h2>
      <p className='text-center mt-3 mb-10 text-base font-medium text-secondary'>{t('activation.notFound.subtitle')}</p>
      <img src={invalidQrCode} alt='chaizer' />
    </div>
  );
}

export default function ItemActivation() {
  const params = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const authenticated = useSelector((state: RootState) => state.auth.authenticated);
  const [loading, setLoading] = useState(true);
  const [itemDetails, setItemDetails] = useState<ItemDetails | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [isActivating, setIsActivating] = useState(false);
  const [activationStatus, setActivationStatus] = useState<string | null>(null);

  useEffect(() => {
    if (!params?.qrCode) {
      setError('QR Code not found in URL');
      setLoading(false);
      return;
    }

    setLoading(true);
    Api.items.getItemDetails(params.qrCode)
      .then((response) => {
        setItemDetails(response);
        setLoading(false);
      })
      .catch((e: any) => {
        setLoading(false);
        setError(e.response?.data?.error_code || 'An error occurred while fetching item details.');
      });
  }, [params?.qrCode]);

  const handleActivate = () => {
    if (!params?.qrCode) return;

    if (!authenticated) {
      saveAuthReturnTo(locationPath(location));
      navigate(`/${params.locale}/auth/login`, { state: { from: location } });
      return;
    }

    setIsActivating(true);
    setError(null);
    Api.items.activateItem({ qr_code: params.qrCode })
      .then(() => {
        setActivationStatus(ACTIVE_STATE_SUCCESS);
      })
      .catch((e: any) => {
        setError(e.response?.data?.error_code || 'Activation failed.');
        setActivationStatus(ACTIVE_STATE_CANT_ACTIVATE);
      })
      .finally(() => {
        setIsActivating(false);
      });
  };

  if (loading) {
    return <Loader />;
  }

  if (activationStatus === ACTIVE_STATE_SUCCESS) {
    return <SuccessView />;
  }

  if (activationStatus === ACTIVE_STATE_CANT_ACTIVATE) {
    return <ActivationErrorView />;
  }

  return (
    <div className='flex h-[calc(100vh-4rem)] flex-col items-center justify-center p-4'>
      {itemDetails ? (
        <ActivationView
          itemDetails={itemDetails}
          handleActivate={handleActivate}
          isActivating={isActivating}
          error={error}
          authenticated={authenticated}
        />
      ) : (
        <NotFoundView />
      )}
    </div>
  );
}
